import { ADMIN_INSPECTOR_STATUSES } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the inspectors list (AUT-125). Mirrors the backend
 * `ListAdminInspectorsQuerySchema` — `status` is a multi-select built from
 * the 10-value `ADMIN_INSPECTOR_STATUSES` (NOT hand-typed; CLAUDE.md rule
 * #11/#7), `q` is a min-3-char free-text search over name + phone + email,
 * `cityCountry` + `city` are picked from the place dictionary (AUT-761;
 * `city` is the key). Every key has a
 * `.catch` default so a malformed / absent query param degrades safely
 * instead of throwing — `useUrlFilters` parses the raw query string
 * straight through.
 */

/** Min free-text search length (mirrors backend MIN_SEARCH_LENGTH). */
export const MIN_SEARCH_LENGTH = 3;

/** `status=active,suspended` → string[] of valid statuses (empty on junk). */
const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) =>
    (raw ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_INSPECTOR_STATUSES)[number] =>
        (ADMIN_INSPECTOR_STATUSES as readonly string[]).includes(s),
      ),
  );

const qSchema = z.string().optional().catch(undefined);
const citySchema = z.string().optional().catch(undefined);
const cityCountrySchema = z.string().optional().catch(undefined);

export const inspectorFiltersSchema = z.object({
  status: statusSchema,
  cityCountry: cityCountrySchema,
  city: citySchema,
  q: qSchema,
});

export type InspectorFilters = z.infer<typeof inspectorFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = ADMIN_INSPECTOR_STATUSES.map((value) => ({
  value,
  labelKey: `inspectors.status.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: InspectorFilters): boolean =>
  (filters.status?.length ?? 0) > 0 ||
  Boolean(filters.cityCountry && filters.cityCountry.length > 0) ||
  Boolean(filters.city && filters.city.length > 0) ||
  Boolean(filters.q && filters.q.length > 0);
